/** Framework-free collection capabilities shared by every host. */

export type CollectionAdapterKind =
  | 'server-paged'
  | 'complete-client'
  | 'capped-client'
  | 'relationship'

export type CollectionCompleteness =
  | { kind: 'server-authoritative' }
  | { kind: 'complete'; rowCount: number }
  | { kind: 'incomplete'; rowCount: number; limit: number; reason: string }
  | { kind: 'pending'; rowCount: number; limit: number }
  | { kind: 'failed'; rowCount: number; limit: number; reason: string }

export interface CollectionCapabilities {
  exactCount: boolean
  globalSort: boolean
  grouping: 'server' | 'client' | false
  savedViewWrites: boolean
  commands: boolean
}

export interface CollectionAdapter {
  kind: CollectionAdapterKind
  completeness: CollectionCompleteness
  capabilities: CollectionCapabilities
  groupingRefusal: string | null
}

/**
 * Predictable local search over an already-authorised, complete client pool.
 * Tokens may span fields and accents do not make displayed data undiscoverable.
 * An adapter still decides whether client search is truthful for its pool.
 */
export function matchesCollectionText(query: string, ...values: unknown[]): boolean {
  const normalize = (value: unknown) => String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
  const tokens = normalize(query).trim().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return true
  const haystack = values.map(normalize).join(' ')
  return tokens.every((token) => haystack.includes(token))
}

interface CollectionAuthority {
  savedViewWrites?: boolean
  commands?: boolean
}

const authority = (input: CollectionAuthority): Pick<CollectionCapabilities, 'savedViewWrites' | 'commands'> => ({
  savedViewWrites: input.savedViewWrites ?? false,
  commands: input.commands ?? false,
})

export function serverPagedCollection(input: CollectionAuthority & {
  exactTotal: boolean
  serverGrouping: boolean
}): CollectionAdapter {
  const grouping = input.exactTotal && input.serverGrouping ? 'server' as const : false
  return {
    kind: 'server-paged',
    completeness: { kind: 'server-authoritative' },
    capabilities: {
      exactCount: input.exactTotal,
      globalSort: true,
      grouping,
      ...authority(input),
    },
    groupingRefusal: grouping ? null : 'This collection has no exact server grouping door.',
  }
}

export function completeClientCollection(input: CollectionAuthority & {
  rowCount: number
}): CollectionAdapter {
  return {
    kind: 'complete-client',
    completeness: { kind: 'complete', rowCount: input.rowCount },
    capabilities: {
      exactCount: true,
      globalSort: true,
      grouping: 'client',
      ...authority(input),
    },
    groupingRefusal: null,
  }
}

export function cappedClientCollection(input: CollectionAuthority & {
  rowCount: number
  limit: number
  reason: string
}): CollectionAdapter {
  const complete = input.rowCount < input.limit
  return {
    kind: 'capped-client',
    completeness: complete
      ? { kind: 'complete', rowCount: input.rowCount }
      : { kind: 'incomplete', rowCount: input.rowCount, limit: input.limit, reason: input.reason },
    capabilities: {
      exactCount: complete,
      globalSort: complete,
      grouping: complete ? 'client' : false,
      ...authority(input),
    },
    groupingRefusal: complete ? null : input.reason,
  }
}

export function relationshipCollection(input: CollectionAuthority & {
  rowCount: number
  limit: number
  settled: boolean
  failed: boolean
  reason: string
}): CollectionAdapter {
  const complete = input.settled && !input.failed && input.rowCount < input.limit
  const completeness: CollectionCompleteness = !input.settled
    ? { kind: 'pending', rowCount: input.rowCount, limit: input.limit }
    : input.failed
      ? { kind: 'failed', rowCount: input.rowCount, limit: input.limit, reason: input.reason }
      : complete
        ? { kind: 'complete', rowCount: input.rowCount }
        : { kind: 'incomplete', rowCount: input.rowCount, limit: input.limit, reason: input.reason }
  return {
    kind: 'relationship',
    completeness,
    capabilities: {
      exactCount: complete,
      globalSort: complete,
      grouping: complete ? 'client' : false,
      ...authority(input),
    },
    groupingRefusal: complete ? null : input.reason,
  }
}

export type CollectionGroupingDecision =
  | { kind: 'none' }
  | { kind: 'supported'; mode: 'server' | 'client'; column: string }
  | { kind: 'unsupported'; column: string; reason: string }

export function resolveCollectionGrouping(input: {
  adapter: CollectionAdapter
  requestedColumn: string | null | undefined
  enumerable: boolean
}): CollectionGroupingDecision {
  if (!input.requestedColumn) return { kind: 'none' }
  if (!input.enumerable) {
    return {
      kind: 'unsupported',
      column: input.requestedColumn,
      reason: 'Only a field with a fixed set of values can group a collection.',
    }
  }
  if (!input.adapter.capabilities.grouping) {
    return {
      kind: 'unsupported',
      column: input.requestedColumn,
      reason: input.adapter.groupingRefusal ?? 'This collection cannot be grouped exactly.',
    }
  }
  return { kind: 'supported', mode: input.adapter.capabilities.grouping, column: input.requestedColumn }
}

export type CollectionIdentityFailure =
  | { kind: 'missing'; index: number }
  | { kind: 'duplicate'; index: number; firstIndex: number; id: string }

export type CollectionIdentityResult<T extends object> =
  | { ok: true; rows: Array<T & { id: string }> }
  | { ok: false; failure: CollectionIdentityFailure }

/**
 * Give a collection one canonical row identity without teaching the grid any
 * domain key. The whole answer is refused if even one row cannot be keyed:
 * dropping just that row would turn an identity defect into missing data.
 */
export function mapCollectionIdentity<T extends object>(
  rows: readonly T[], select: (row: T) => unknown,
): CollectionIdentityResult<T> {
  const seen = new Map<string, number>()
  const mapped: Array<T & { id: string }> = []
  for (const [index, row] of rows.entries()) {
    const raw = select(row)
    if (raw == null || String(raw).trim() === '') return { ok: false, failure: { kind: 'missing', index } }
    const id = String(raw)
    const firstIndex = seen.get(id)
    if (firstIndex != null) return { ok: false, failure: { kind: 'duplicate', index, firstIndex, id } }
    seen.set(id, index)
    mapped.push({ ...row, id })
  }
  return { ok: true, rows: mapped }
}

declare const durableCollectionKeyBrand: unique symbol
declare const transientCollectionKeyBrand: unique symbol

export type DurableCollectionKey = string & { readonly [durableCollectionKeyBrand]: true }
export type TransientCollectionKey = string & { readonly [transientCollectionKeyBrand]: true }

export function durableCollectionKey(input:
  | { host: string; entity: string }
  | { host: string; parentEntity: string; slot: string },
): DurableCollectionKey {
  return JSON.stringify('entity' in input
    ? ['collection', input.host, input.entity]
    : ['relationship', input.host, input.parentEntity, input.slot]) as DurableCollectionKey
}

export function transientCollectionKey(input: {
  path: readonly string[]
  viewId: string
}): TransientCollectionKey {
  return JSON.stringify(['session', ...input.path, input.viewId]) as TransientCollectionKey
}

export interface CollectionSortTerm {
  key: string
  direction: 'asc' | 'desc'
}

export interface CollectionViewDefaults<F> {
  viewId: string | null
  search?: string
  sort: readonly CollectionSortTerm[]
  filters: F
  hiddenColumns?: readonly string[]
  groupBy?: string | null
}

export interface CollectionScopeState<F> {
  viewId: string | null
  search: string
  sort: readonly CollectionSortTerm[]
  filters: F
  hiddenColumns: readonly string[]
  /** `undefined` follows the view; `null` explicitly removes its grouping. */
  groupBy?: string | null
  selection: readonly string[]
  pinnedRows: readonly string[]
  pinnedColumns: readonly string[]
  widths: Readonly<Record<string, number>>
  pageOffset: number
}

export type ScopedCollectionState<F> = Readonly<Partial<Record<TransientCollectionKey, CollectionScopeState<F>>>>

export type CollectionAction<F> =
  | {
    type: 'patch'
    scope: TransientCollectionKey
    defaults: CollectionViewDefaults<F>
    patch: Partial<CollectionScopeState<F>>
  }
  | {
    type: 'adopt-view'
    scope: TransientCollectionKey
    viewId: string | null
    defaults: CollectionViewDefaults<F>
  }
  | { type: 'reset'; scope: TransientCollectionKey; defaults: CollectionViewDefaults<F> }
  | { type: 'drop'; scope: TransientCollectionKey }

export function collectionScopeState<F>(defaults: CollectionViewDefaults<F>): CollectionScopeState<F> {
  return {
    viewId: defaults.viewId,
    search: defaults.search ?? '',
    sort: defaults.sort,
    filters: defaults.filters,
    hiddenColumns: defaults.hiddenColumns ?? [],
    groupBy: undefined,
    selection: [],
    pinnedRows: [],
    pinnedColumns: [],
    widths: {},
    pageOffset: 0,
  }
}

export function collectionReducer<F>(
  state: ScopedCollectionState<F>, action: CollectionAction<F>,
): ScopedCollectionState<F> {
  if (action.type === 'drop') {
    if (!(action.scope in state)) return state
    const next = { ...state }
    delete next[action.scope]
    return next
  }
  if (action.type === 'reset') {
    return { ...state, [action.scope]: collectionScopeState(action.defaults) }
  }
  if (action.type === 'adopt-view') {
    return {
      ...state,
      [action.scope]: collectionScopeState({ ...action.defaults, viewId: action.viewId }),
    }
  }
  return {
    ...state,
    [action.scope]: {
      ...(state[action.scope] ?? collectionScopeState(action.defaults)),
      ...action.patch,
    },
  }
}

export function selectCollectionScope<F>(
  state: ScopedCollectionState<F>, scope: TransientCollectionKey, defaults: CollectionViewDefaults<F>,
): CollectionScopeState<F> {
  return state[scope] ?? collectionScopeState(defaults)
}

export function selectCollectionGroupBy<F>(
  state: CollectionScopeState<F>, defaults: CollectionViewDefaults<F>,
): string | null {
  return state.groupBy === undefined ? (defaults.groupBy ?? null) : state.groupBy
}

export function selectVisibleCollectionColumns<F>(
  columns: readonly string[], state: CollectionScopeState<F>,
): string[] {
  const hidden = new Set(state.hiddenColumns)
  return columns.filter((column) => !hidden.has(column))
}

export function collectionIsModified<F>(
  state: CollectionScopeState<F>, defaults: CollectionViewDefaults<F>,
  equalFilters: (left: F, right: F) => boolean = (left, right) => JSON.stringify(left) === JSON.stringify(right),
): boolean {
  return state.search !== (defaults.search ?? '')
    || JSON.stringify(state.sort) !== JSON.stringify(defaults.sort)
    || !equalFilters(state.filters, defaults.filters)
    || JSON.stringify(state.hiddenColumns) !== JSON.stringify(defaults.hiddenColumns ?? [])
    || state.groupBy !== undefined
}

export function collectionQueryIdentity<F>(
  state: CollectionScopeState<F>, defaults: CollectionViewDefaults<F>,
): readonly unknown[] {
  return [
    state.viewId,
    state.search,
    state.sort,
    state.filters,
    selectCollectionGroupBy(state, defaults),
    state.pageOffset,
  ] as const
}
